import './Profile.css';
import {useState, useEffect, useReducer} from 'react';
import { format } from 'date-fns'
import { IoClose } from "react-icons/io5";
import { Container, Grid, Typography, Button, Alert, IconButton, Collapse, CircularProgress, Skeleton  } from '@mui/material';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import Username from '../../Components/Username/Username';
import EditableLinks from '../../Components/EditableLinks/EditableLinks';
import EditableTheme from '../../Components/EditableTheme/EditableTheme';
import EditableInfo from '../../Components/EditableInfo/EditableInfo';
import PreviewLinks from '../../Components/PreviewLinks/PreviewLinks';
import { isValidateDesc, isValidateTitle } from '../../utils';
import { initialState, profileReducer } from '../../reducer/profileReducer';
import es from 'date-fns/locale/es';
import en from 'date-fns/locale/en-US';
import { Link } from 'react-router-dom';
import apiClient from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { addLink, deleteLink, editInfo, editLink, editPosition, editTheme, updateDraft, updateLastProfile, updatePublish } from '../../reducer/profileActions';

const locales = {
	es: es,
	en: en
};

const socialMediaSchema = yup.object({
  icon: yup
    .string()
    .required(),
});

function Profile() {
  const { t, i18n } = useTranslation();
  const { user: {username} } = useAuth();
  const { showToast } = useToast();
  const [profileDataState, dispatch] = useReducer(profileReducer, initialState);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [existChange, setExistChange] = useState(false);
  const [openDeployed, setOpenDeployed] = useState(false);

  const linkSchema = yup.object({
    link: yup
      .string(t("profile.link-string"))
      .url()
      .required(),
  });

  useEffect(() => {
    // if(window != null){
    //     window.scrollTo({top: 0, behavior: 'smooth'});
    // }
    apiClient.get("/profile/get") 
    .then(function (response) {
      const { data } = response;
      if (response.status === 200 && data.status === 1){
        let linksBefore = [];
        if (data.result.links.length === 0) {
          linksBefore = [{
            id_link: parseInt(Math.floor(Math.random() * Date.now()).toString().slice(0, 5)),
            social_url: "",
            position: 0,
            social_name: "",
            icon_name: "",
            icon_id: null
          }]
        } else if (data.result.links) {
          linksBefore = data.result.links.sort((a, b) => {
            return a.position - b.position
          });
        } else {
          linksBefore = []
        }
        dispatch({
          type: 'SET_PROFILE_DATA',
          payload: {
            links: linksBefore,
            theme: data.result.theme ? data.result.theme : {
              title_color_user: "rgba(255, 255, 255, 1)",
              text_color_button: "rgba(255, 255, 255, 1)",
              background_color_button: "rgba(255, 255, 255, 0)",
              border_button: "1px solid rgba(255, 255, 255, 1)",
              left_gradient: "rgba(58, 97, 134, 1)",
              right_gradient: "rgba(137, 37, 62, 1)"
            },
            info: data.result.info ? data.result.info : {
              title: '',
              description: ''
            },
            isPublished: data.result.isPublished ? data.result.isPublished : false,
            publishDate: data.result.publishDate ? data.result.publishDate : null,
          }
        });

        setLoading(false);
        setTimeout(() => {
          data.result.isPublished && setOpenDeployed(true);
        }, 1000)
      }
      if(response.status === 200 && data.status === -1){
        //usuario no encontrado
        setLoading(false);
        showToast('error', t("profile.error.internal-error"));
      }
    })
    .catch(function (error) {
      console.log(error);
      setLoading(false);
      showToast('error', t("profile.error.internal-error"));
    });
  // eslint-disable-next-line
  }, []);

  const statusLink = link => linkSchema.isValidSync({ link: link.social_url });
  const statusSocialMedia = socialMedia => socialMediaSchema.isValidSync({ icon: socialMedia.icon_id });

  const completeVeryfier = profile => {
    if (profile.links == null || profile.links.length === 0) {
      return {valid: false, message: t("profile.veryfier.links-length")};
    }
    if (!profile.links.every(statusLink)) {
      return {valid: false, message: t("profile.veryfier.links-status")};
    }
    if (!profile.links.every(statusSocialMedia)) {
      return {valid: false, message: t("profile.veryfier.social-media-status")};
    }
    if (!isValidateTitle(profile.info.title)) {
      return {valid: false, message: t("profile.veryfier.title-length")};
    }
    if (!isValidateDesc(profile.info.description)) {
      return {valid: false, message: t("profile.veryfier.desc-length")};
    }
    return {valid: true};
  }

  const handleDeploy = () => {
    const resultVeryfier = completeVeryfier(profileDataState.currentProfile);
    if (resultVeryfier.valid) {
      setPending(!pending);
      const publishDate = new Date();
      apiClient.patch("/profile/update", {
        "links": profileDataState.currentProfile.links,
        "theme": profileDataState.currentProfile.theme,
        "info": profileDataState.currentProfile.info,
        "publishDate": publishDate,
        "isPublished": true
      })
      .then(function (response) {
          const data = response.data;
          if (response.status === 200 && data.status === 1){
            showToast('success', t("profile.success.published"));
            updatePublish(dispatch, {isPublished: true, publishDate: `${publishDate}`});
            updateLastProfile(dispatch, profileDataState.currentProfile);
            setExistChange(false);
            updateDraft(dispatch, false);
          }
          if (response.status === 400 && data.status === -1){
            showToast('error', t("profile.error.internal-error"));
          }
          setPending(false);
      })
      .catch(function (error) {
          showToast('error', t("profile.error.internal-error"));
          setPending(false);
      });
    } else {
      showToast('error', resultVeryfier.message);
    }
  }

  useEffect(() => {
    if (JSON.stringify(profileDataState.lastProfile) !== JSON.stringify(profileDataState.currentProfile)) {
      setExistChange(true);
      setOpenDeployed(true);
    } else if (JSON.stringify(profileDataState.lastProfile) === JSON.stringify(profileDataState.currentProfile)) {
      setExistChange(false);
      if (!profileDataState.isPublished) {
        setOpenDeployed(false);
      }
    }
  }, [profileDataState])

  return (
    <div className="section-profile">
      {
        loading ?
        <div className='section-profile-container'>
          <Container className='section-profile-body' maxWidth="lg">
            <Grid
              container
              className="profile-grid-container"
              spacing={2}
            >
              <Grid item className='title-profile-grid-skeleton' size={{ xs: 12, md: 12 }}>
                <Typography className='title-profile-skeleton' variant="h3" gutterBottom component="h3" align='center'>
                  {t("profile.title")}
                </Typography>
              </Grid>
              <Grid className='profile-grid-skeleton' item size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" />
                <Skeleton variant="rectangular"/>
              </Grid>
              <Grid className='profile-grid-skeleton' item size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular"/>
                <Skeleton variant="rectangular"/>
              </Grid>
            </Grid>
          </Container>
        </div>
        :
        <div className='section-profile-container'>
          <Container className='section-profile-body' maxWidth="lg">
            <Grid
              container
              className="profile-grid-container"
              spacing={2}
            >
              <Grid item size={{ xs: 12, md: 12 }}>
                <Typography className='title-profile' variant="h3" component="h3" align='center'>
                  {t("profile.title")}
                </Typography>
              </Grid>
              <Grid className='sp-grid-alert-action' item size={{ xs: 12, md: 12 }}>
                  <Collapse in={openDeployed}>
                    <Alert
                      className='section-profile-alert-action'
                      icon={false}
                      variant="filled"
                      severity={!existChange && profileDataState.isPublished ? 'success' : 'info'}
                      action={
                        !existChange && profileDataState.isPublished ?
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          size="small"
                          onClick={() => {
                            setOpenDeployed(false);
                          }}
                        >
                          <IoClose />
                        </IconButton> :
                        undefined
                      }
                    >
                      {
                        existChange ?
                        t("profile.publish-links")
                        :
                        profileDataState.isPublished ? 
                        <span>{t("profile.published-links")} {format(new Date(profileDataState.publishDate), 'PPPppp', {locale: locales[i18n.language]})}.</span>
                        :
                        ''
                      }
                      {
                      (existChange) && <Button variant="contained" onClick={handleDeploy} disabled={pending}>
                        {pending && <CircularProgress size={24} color="secondary" />}
                        {!pending && (t("profile.commit-2"))}
                      </Button>
                      }
                      {
                        (!existChange && profileDataState.isPublished) && 
                        <Button variant="contained" sx={{ color:'#1a1a1a' }} component={Link} to={`/${username}`}>
                          {t("profile.see-now")}
                        </Button>
                      }
                    </Alert>
                  </Collapse>
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Username/>
                <EditableInfo
                  currentInfo={profileDataState.currentProfile.info}
                  onHandleEditInfo={(type, value) => editInfo(dispatch, type, value)}
                />
                <EditableLinks
                  lastLinks={profileDataState.lastProfile.links}
                  currentLinks={profileDataState.currentProfile.links}
                  onHandleAddLink={link => addLink(dispatch, link)}
                  onHandleDeleteLink={link => deleteLink(dispatch, link)}
                  onHandleEditLink={(type, link) => editLink(dispatch, type, link)}
                  onHandleEditPosition={(destination, source) => editPosition(dispatch, destination, source)}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <EditableTheme
                  onHandleEditTheme={(type, value) => editTheme(dispatch, type, value)}
                  baseTitleClrUsr={profileDataState.currentProfile.theme.title_color_user.match(/(\d+(?:\.\d+)?)/g)}
                  baseButtonTxtClr={profileDataState.currentProfile.theme.text_color_button.match(/(\d+(?:\.\d+)?)/g)}
                  baseBackgroundClrBtn={profileDataState.currentProfile.theme.background_color_button.match(/(\d+(?:\.\d+)?)/g)}
                  baseBorderBtn={profileDataState.currentProfile.theme.border_button.slice(4).match(/(\d+(?:\.\d+)?)/g)}
                  baseBorderWidth={profileDataState.currentProfile.theme.border_button.split(' ')[0]}
                  baseLeftGrd={profileDataState.currentProfile.theme.left_gradient.match(/(\d+(?:\.\d+)?)/g)}
                  baseRightGrd={profileDataState.currentProfile.theme.right_gradient.match(/(\d+(?:\.\d+)?)/g)}
                />
                <PreviewLinks
                  links={profileDataState.currentProfile.links}
                  theme={profileDataState.currentProfile.theme}
                  info={profileDataState.currentProfile.info}
                />
              </Grid>
            </Grid>
          </Container>
        </div>
      }
    </div>
  );
}

export default Profile;
